import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useParams } from 'react-router';
import { Card, CardHeader, Stack } from '@mui/material';

import { QueryRenderer } from '@barboxx/admin-components';
import { useAuth } from '@barboxx/admin-utils';
import Api from '@barboxx/api';

import { FinancialContent } from './FinancialContent';
import { NoStripeData } from './NoStripeData';

export function Financial() {
    const { auth } = useAuth();
    const { id } = useParams<{ id: string }>();
    const businessId = Number(id);

    const businessQueryResults = useQuery({
        queryKey: ['businessStripe', businessId],
        queryFn: () => Api.Stripe.getStripeAccount(businessId),
    });

    const getHeadingText = React.useCallback(() => {
        const details = businessQueryResults.data?.data;

        if (details?.stripeAccountId) {
            if (details.detailsSubmitted) {
                return 'Update Stripe Account or Payment Information';
            }
            return 'Add information to finish account linking';
        }

        const isBusinessOwner =
            auth.user?.userType === 'Business' &&
            auth.user?.roles?.includes('Owner');

        if (auth.user?.userType === 'Internal') {
            return 'Owner setup required';
        }

        if (auth.user?.userType === 'Business' && !isBusinessOwner) {
            return 'Owner setup required';
        }

        return 'Create a BarBoxx Business Account';
    }, [
        auth.user?.roles,
        auth.user?.userType,
        businessQueryResults.data?.data,
    ]);

    return (
        <Stack gap={2}>
            <Card>
                <CardHeader title={getHeadingText()} />
                <QueryRenderer
                    results={businessQueryResults}
                    NoDataComponent={() => (
                        <NoStripeData
                            businessId={businessId}
                            refetch={businessQueryResults.refetch}
                        />
                    )}
                >
                    {(data, actions) => (
                        <FinancialContent
                            stripeAccount={data}
                            businessId={businessId}
                            refetch={actions.refetch}
                        />
                    )}
                </QueryRenderer>
            </Card>
        </Stack>
    );
}
